import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Badge,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Clear as ClearIcon,

  Image as ImageIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  PhotoLibrary as PhotoLibraryIcon
} from '@mui/icons-material';
import { GetApp as GetAppIcon, Publish as PublishIcon } from '@mui/icons-material';
import { CoilAlternative, CoilImage } from '../../types/maintenanceBank';
import { CoilAlternativeService } from '../../services/maintenanceBankService';

const CoilAlternativesPage: React.FC = () => {
  const [coilAlternatives, setCoilAlternatives] = useState<CoilAlternative[]>([]);
  const [filteredCoilAlternatives, setFilteredCoilAlternatives] = useState<CoilAlternative[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // البحث والفلترة
  const [searchTerm, setSearchTerm] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>('all');

  // حوارات
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoil, setEditingCoil] = useState<CoilAlternative | null>(null);

  // عرض الصور
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImages, setSelectedImages] = useState<CoilImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  // بيانات النموذج
  const [coilForm, setCoilForm] = useState({
    originalCoilNumber: '',
    alternativeCoilNumber: '',
    originalRuler: '',
    alternativeRuler: '',
    notes: '',
    verified: false,
    deviceTypes: [] as string[],
    brands: [] as string[],
    images: [] as CoilImage[]
  });

  // تحميل البيانات عند بدء الصفحة
  useEffect(() => {
    loadCoilAlternatives();
  }, []);

  // تطبيق البحث والفلترة عند تغيير البيانات أو معايير البحث
  useEffect(() => {
    filterCoilAlternatives();
  }, [coilAlternatives, searchTerm, verifiedFilter]);

  const filterCoilAlternatives = () => {
    let filtered = [...coilAlternatives];

    // تطبيق البحث النصي
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(coil =>
        coil.originalCoilNumber.toLowerCase().includes(searchLower) ||
        coil.alternativeCoilNumber.toLowerCase().includes(searchLower) ||
        (coil.originalRuler && coil.originalRuler.toLowerCase().includes(searchLower)) ||
        (coil.alternativeRuler && coil.alternativeRuler.toLowerCase().includes(searchLower)) ||
        (coil.notes && coil.notes.toLowerCase().includes(searchLower)) ||
        coil.deviceTypes.some(type => type.toLowerCase().includes(searchLower)) ||
        coil.brands.some(brand => brand.toLowerCase().includes(searchLower)) ||
        // البحث في أوصاف الصور
        coil.images.some(image =>
          (image.description && image.description.toLowerCase().includes(searchLower)) ||
          image.type.toLowerCase().includes(searchLower)
        )
      );
    }

    // تطبيق فلتر التحقق
    if (verifiedFilter !== 'all') {
      filtered = filtered.filter(coil =>
        verifiedFilter === 'verified' ? coil.verified : !coil.verified
      );
    }

    setFilteredCoilAlternatives(filtered);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setVerifiedFilter('all');
  };

  // تصدير البدائل إلى ملف إكسل
  const exportToExcel = () => {
    try {
      const rows = (filteredCoilAlternatives.length > 0 ? filteredCoilAlternatives : coilAlternatives).map((c) => ({
        'رقم الكوف الأصلي': c.originalCoilNumber || '',
        'رقم الكوف البديل': c.alternativeCoilNumber || '',
        'المسطرة الأصلية': c.originalRuler || '',
        'المسطرة البديلة': c.alternativeRuler || '',
        'محقق': c.verified ? 'نعم' : 'لا',
        'أنواع الأجهزة': (c.deviceTypes || []).join(', '),
        'الماركات': (c.brands || []).join(', '),
        'ملاحظات': c.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'بدائل الكوفات');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `coil_alternatives_${dateStr}.xlsx`);
    } catch (e) {
      console.error('خطأ أثناء التصدير إلى إكسل:', e);
      setError('حدث خطأ أثناء تصدير البيانات إلى إكسل');
    }
  };

  // استيراد البدائل من ملف إكسل
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          let importedCount = 0;
          for (const row of json) {
            const getVal = (keys: string[]) => {
              for (const k of keys) {
                if (row[k] !== undefined && row[k] !== null) return String(row[k]).trim();
              }
              return '';
            };

            const originalCoilNumber = getVal(['رقم الكوف الأصلي', 'originalCoilNumber', 'OriginalCoilNumber']);
            const alternativeCoilNumber = getVal(['رقم الكوف البديل', 'alternativeCoilNumber', 'AlternativeCoilNumber']);
            const originalRuler = getVal(['المسطرة الأصلية', 'originalRuler', 'OriginalRuler']);
            const alternativeRuler = getVal(['المسطرة البديلة', 'alternativeRuler', 'AlternativeRuler']);
            const verifiedStr = getVal(['محقق', 'verified', 'Verified']).toLowerCase();
            const deviceTypesStr = getVal(['أنواع الأجهزة', 'deviceTypes', 'DeviceTypes']);
            const brandsStr = getVal(['الماركات', 'brands', 'Brands']);
            const notes = getVal(['ملاحظات', 'notes', 'Notes']);

            if (!originalCoilNumber || !alternativeCoilNumber) {
              // تخطي الصفوف غير المكتملة
              continue;
            }

            const toBool = (val: string) => ['true', 'نعم', 'yes', '1'].includes(val);

            const coilData = {
              originalCoilNumber,
              alternativeCoilNumber,
              originalRuler,
              alternativeRuler,
              notes,
              verified: toBool(verifiedStr),
              deviceTypes: deviceTypesStr ? deviceTypesStr.split(',').map((s) => s.trim()).filter(Boolean) : [],
              brands: brandsStr ? brandsStr.split(',').map((s) => s.trim()).filter(Boolean) : [],
              images: [],
              compatibility: 100,
              createdBy: 'import',
              branchId: 'import'
            } as any;

            try {
              await CoilAlternativeService.addCoilAlternative(coilData);
              importedCount++;
            } catch (addErr) {
              console.error('فشل إضافة صف أثناء الاستيراد:', addErr);
            }
          }

          await loadCoilAlternatives();
          window.alert(`تم استيراد ${importedCount} بدائل بنجاح`);
        } catch (err) {
          console.error('خطأ في معالجة ملف الإكسيل:', err);
          setError('حدث خطأ أثناء قراءة ملف الإكسيل');
        } finally {
          // إعادة تعيين قيمة input
          event.target.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('خطأ أثناء الاستيراد من إكسل:', error);
      setError('حدث خطأ أثناء الاستيراد من إكسل');
    }
  };

  // عرض الصور
  const handleViewImages = (images: CoilImage[]) => {
    setSelectedImages(images);
    setCurrentImageIndex(0);
    setShowImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev < selectedImages.length - 1 ? prev + 1 : 0
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : selectedImages.length - 1
    );
  };

  // مكون عرض الكارد للكوف
  const CoilCard: React.FC<{ coil: CoilAlternative }> = ({ coil }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {coil.images.length > 0 && (
        <CardMedia
          component="img"
          height="200"
          image={coil.images[0].url}
          alt={coil.images[0].description || `صورة ${coil.originalCoilNumber}`}
          onClick={() => handleViewImages(coil.images)}
          sx={{
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" component="h3">
            {coil.originalCoilNumber}
          </Typography>
          {coil.verified && (
            <Chip
              icon={<VerifiedIcon />}
              label="محقق"
              color="success"
              size="small"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          البديل: {coil.alternativeCoilNumber}
        </Typography>

        {(coil.originalRuler || coil.alternativeRuler) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            المسطرة: {coil.originalRuler} → {coil.alternativeRuler}
          </Typography>
        )}

        {coil.deviceTypes.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              أنواع الأجهزة:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {coil.deviceTypes.slice(0, 3).map((type, index) => (
                <Chip key={index} label={type} size="small" variant="outlined" />
              ))}
              {coil.deviceTypes.length > 3 && (
                <Chip label={`+${coil.deviceTypes.length - 3}`} size="small" variant="outlined" />
              )}
            </Box>
          </Box>
        )}

        {coil.images.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Badge badgeContent={coil.images.length} color="primary">
              <PhotoLibraryIcon color="action" />
            </Badge>
            <Typography variant="caption" color="text.secondary">
              {coil.images.length} صورة
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions>
        <Button
          size="small"
          startIcon={<ZoomInIcon />}
          onClick={() => handleViewImages(coil.images)}
          disabled={coil.images.length === 0}
        >
          عرض الصور
        </Button>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEdit(coil)}
        >
          تعديل
        </Button>
      </CardActions>
    </Card>
  );

  const loadCoilAlternatives = async () => {
    try {
      setLoading(true);
      const result = await CoilAlternativeService.getCoilAlternatives();
      setCoilAlternatives(result.items);
      setFilteredCoilAlternatives(result.items); // تعيين البيانات المفلترة أيضاً
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCoil(null);
    setCoilForm({
      originalCoilNumber: '',
      alternativeCoilNumber: '',
      originalRuler: '',
      alternativeRuler: '',
      notes: '',
      verified: false,
      deviceTypes: [],
      brands: [],
      images: []
    });
    setShowDialog(true);
  };

  const handleEdit = (coil: CoilAlternative) => {
    setEditingCoil(coil);
    setCoilForm({
      originalCoilNumber: coil.originalCoilNumber,
      alternativeCoilNumber: coil.alternativeCoilNumber,
      originalRuler: coil.originalRuler || '',
      alternativeRuler: coil.alternativeRuler || '',
      notes: coil.notes || '',
      verified: coil.verified,
      deviceTypes: coil.deviceTypes,
      brands: coil.brands,
      images: coil.images
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const coilData = {
        ...coilForm,
        compatibility: 100, // قيمة افتراضية لنسبة التوافق
        createdBy: 'current-user', // يجب استبداله بالمستخدم الحالي
        branchId: 'current-branch' // يجب استبداله بالفرع الحالي
      };

      if (editingCoil) {
        await CoilAlternativeService.updateCoilAlternative(editingCoil.id, coilData);
      } else {
        await CoilAlternativeService.addCoilAlternative(coilData);
      }

      setShowDialog(false);
      loadCoilAlternatives();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDelete = async (coil: CoilAlternative) => {
    if (window.confirm(`هل أنت متأكد من حذف بديل الكوف "${coil.originalCoilNumber}"؟`)) {
      try {
        await CoilAlternativeService.deleteCoilAlternative(coil.id);
        loadCoilAlternatives();
      } catch (error: any) {
        setError(error.message);
      }
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setLoading(true);
      const uploadedImages: CoilImage[] = [];

      // رفع كل صورة
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        try {
          // استيراد خدمة رفع الصور
          const imageService = await import('../../services/imageService');

          // رفع الصورة مع تسمية مناسبة
          const imageUrl = await imageService.default.uploadImage(file, `coil-alternatives/${coilForm.originalCoilNumber || 'unknown'}`);

          // إنشاء كائن الصورة
          const newImage: CoilImage = {
            id: `img_${Date.now()}_${i}`,
            url: imageUrl,
            type: 'coil', // نوع افتراضي، يمكن تغييره لاحقاً
            description: `صورة ${coilForm.originalCoilNumber || 'الكوف'} - ${file.name}`,
            isPrimary: i === 0 // الصورة الأولى تكون رئيسية
          };

          uploadedImages.push(newImage);
        } catch (uploadError) {
          console.error(`فشل في رفع الصورة ${file.name}:`, uploadError);
          setError(`فشل في رفع الصورة ${file.name}. يرجى المحاولة مرة أخرى.`);
        }
      }

      // إضافة الصور المرفوعة إلى النموذج
      if (uploadedImages.length > 0) {
        setCoilForm(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedImages]
        }));
      }

    } catch (error: any) {
      console.error('خطأ في رفع الصور:', error);
      setError('حدث خطأ أثناء رفع الصور. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
      // إعادة تعيين قيمة input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // حذف صورة من النموذج
  const handleRemoveImage = (imageId: string) => {
    setCoilForm(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  // تحديث نوع الصورة
  const handleImageTypeChange = (imageId: string, newType: CoilImage['type']) => {
    setCoilForm(prev => ({
      ...prev,
      images: prev.images.map(img =>
        img.id === imageId ? { ...img, type: newType } : img
      )
    }));
  };

  // تحديث وصف الصورة
  const handleImageDescriptionChange = (imageId: string, newDescription: string) => {
    setCoilForm(prev => ({
      ...prev,
      images: prev.images.map(img =>
        img.id === imageId ? { ...img, description: newDescription } : img
      )
    }));
  };

  // تعيين صورة كرئيسية
  const handleSetPrimaryImage = (imageId: string) => {
    setCoilForm(prev => ({
      ...prev,
      images: prev.images.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      }))
    }));
  };



  if (loading && coilAlternatives.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* العنوان والأزرار */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5">بدائل الكوفات والمساطر</Typography>
            <Chip label={`إجمالي: ${coilAlternatives.length}`} color="primary" variant="outlined" />
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="عرض كجدول">
              <Button
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
                size="small"
              >
                جدول
              </Button>
            </Tooltip>
            <Tooltip title="عرض كبطاقات">
              <Button
                variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('cards')}
                size="small"
              >
                كارد
              </Button>
            </Tooltip>
            <Tooltip title="تصدير البدائل إلى ملف إكسل">
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={exportToExcel}
                size="small"
              >
                تصدير
              </Button>
            </Tooltip>
            <Tooltip title="استيراد البدائل من ملف إكسل">
              <Button
                variant="outlined"
                component="label"
                startIcon={<PublishIcon />}
                size="small"
              >
                استيراد
                <input type="file" hidden accept=".xlsx,.xls" onChange={handleImportExcel} />
              </Button>
            </Tooltip>
            <Tooltip title="إضافة بديل جديد">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
              >
                إضافة بديل
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {/* شريط البحث والفلترة */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* حقل البحث */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="البحث في أرقام الكوفات، المساطر، الملاحظات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                )
              }}
              size="small"
            />
          </Grid>

          {/* فلتر التحقق */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="حالة التحقق"
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value as any)}
              size="small"
              SelectProps={{ native: true }}
            >
              <option value="all">جميع الحالات</option>
              <option value="verified">محقق فقط</option>
              <option value="unverified">غير محقق فقط</option>
            </TextField>
          </Grid>



          {/* زر مسح الفلاتر */}
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearSearch}
              disabled={!searchTerm && verifiedFilter === 'all'}
            >
              مسح الفلاتر
            </Button>
          </Grid>
        </Grid>

        {/* إحصائيات البحث */}
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            عرض {filteredCoilAlternatives.length} من أصل {coilAlternatives.length} بديل
          </Typography>

          {(searchTerm || verifiedFilter !== 'all') && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {searchTerm && (
                <Chip
                  label={`البحث: "${searchTerm}"`}
                  size="small"
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                />
              )}
              {verifiedFilter !== 'all' && (
                <Chip
                  label={`التحقق: ${verifiedFilter === 'verified' ? 'محقق' : 'غير محقق'}`}
                  size="small"
                  onDelete={() => setVerifiedFilter('all')}
                  color="secondary"
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* عرض البيانات */}
      {viewMode === 'table' ? (
        /* جدول البدائل */
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader sx={{ '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
          <TableHead>
            <TableRow>
              <TableCell>رقم الكوف الأصلي</TableCell>
              <TableCell>رقم الكوف البديل</TableCell>
              <TableCell>المسطرة الأصلية</TableCell>
              <TableCell>المسطرة البديلة</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الصور</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCoilAlternatives.map((coil) => (
              <TableRow key={coil.id}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {coil.originalCoilNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="primary">
                    {coil.alternativeCoilNumber}
                  </Typography>
                </TableCell>
                <TableCell>{coil.originalRuler || '-'}</TableCell>
                <TableCell>{coil.alternativeRuler || '-'}</TableCell>
                <TableCell>
                  {coil.verified ? (
                    <Chip
                      icon={<VerifiedIcon />}
                      label="محقق"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label="غير محقق"
                      color="warning"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {coil.images.length > 0 ? (
                      <>
                        {coil.images.slice(0, 3).map((image, index) => (
                          <Box
                            key={index}
                            component="img"
                            src={image.url}
                            alt={image.description || `صورة ${coil.originalCoilNumber}`}
                            onClick={() => handleViewImages(coil.images)}
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: 2
                              }
                            }}
                          />
                        ))}
                        {coil.images.length > 3 && (
                          <Box
                            onClick={() => handleViewImages(coil.images)}
                            sx={{
                              width: 50,
                              height: 50,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'primary.light',
                              color: 'white',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            +{coil.images.length - 3}
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleViewImages(coil.images)}
                          sx={{ ml: 1 }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon sx={{ color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          لا توجد صور
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(coil)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(coil)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      ) : (
        /* عرض الكارد */
        <Grid container spacing={3}>
          {filteredCoilAlternatives.map((coil) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={coil.id}>
              <CoilCard coil={coil} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* رسائل عدم وجود بيانات */}
      {coilAlternatives.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            لا توجد بدائل كوفات مسجلة
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            انقر على "إضافة بديل جديد" لإضافة أول بديل
          </Typography>
        </Paper>
      )}

      {coilAlternatives.length > 0 && filteredCoilAlternatives.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج للبحث
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            جرب تغيير كلمات البحث أو الفلاتر المطبقة
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearSearch}
            sx={{ mt: 2 }}
          >
            مسح جميع الفلاتر
          </Button>
        </Paper>
      )}

      {/* حوار إضافة/تعديل بديل الكوف */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoil ? 'تعديل بديل الكوف' : 'إضافة بديل كوف جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="رقم الكوف الأصلي"
                value={coilForm.originalCoilNumber}
                onChange={(e) => setCoilForm({ ...coilForm, originalCoilNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="رقم الكوف البديل"
                value={coilForm.alternativeCoilNumber}
                onChange={(e) => setCoilForm({ ...coilForm, alternativeCoilNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="المسطرة الأصلية"
                value={coilForm.originalRuler}
                onChange={(e) => setCoilForm({ ...coilForm, originalRuler: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="المسطرة البديلة"
                value={coilForm.alternativeRuler}
                onChange={(e) => setCoilForm({ ...coilForm, alternativeRuler: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography>محقق:</Typography>
                <Button
                  variant={coilForm.verified ? "contained" : "outlined"}
                  onClick={() => setCoilForm({ ...coilForm, verified: !coilForm.verified })}
                  color={coilForm.verified ? "success" : "primary"}
                >
                  {coilForm.verified ? "نعم" : "لا"}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات"
                value={coilForm.notes}
                onChange={(e) => setCoilForm({ ...coilForm, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            
            {/* قسم الصور */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                الصور
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  رفع صور
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>
              
              {coilForm.images.length > 0 && (
                <Grid container spacing={2}>
                  {coilForm.images.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="140"
                          image={image.url}
                          alt={image.description}
                        />
                        <CardContent sx={{ p: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="وصف الصورة"
                            value={image.description || ''}
                            onChange={(e) => handleImageDescriptionChange(image.id, e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            select
                            label="نوع الصورة"
                            value={image.type}
                            onChange={(e) => handleImageTypeChange(image.id, e.target.value as CoilImage['type'])}
                          >
                            <MenuItem value="coil">كوف</MenuItem>
                            <MenuItem value="ruler">مسطرة</MenuItem>
                            <MenuItem value="alternative_coil">كوف بديل</MenuItem>
                            <MenuItem value="alternative_ruler">مسطرة بديلة</MenuItem>
                            <MenuItem value="installation">تركيب</MenuItem>
                          </TextField>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                          <Button
                            size="small"
                            variant={image.isPrimary ? "contained" : "outlined"}
                            onClick={() => handleSetPrimaryImage(image.id)}
                          >
                            {image.isPrimary ? "رئيسية" : "جعل رئيسية"}
                          </Button>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveImage(image.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>إلغاء</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCoil ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* عارض الصور */}
      <Dialog
        open={showImageViewer}
        onClose={handleCloseImageViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PhotoLibraryIcon />
            <Typography variant="h6">
              صور الكوف ({currentImageIndex + 1} من {selectedImages.length})
            </Typography>
          </Box>
          <IconButton onClick={handleCloseImageViewer} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '60vh' }}>
          {selectedImages.length > 0 && (
            <>
              <Box
                component="img"
                src={selectedImages[currentImageIndex]?.url}
                alt={selectedImages[currentImageIndex]?.description || 'صورة الكوف'}
                sx={{
                  width: '100%',
                  height: '60vh',
                  objectFit: 'contain',
                  bgcolor: 'black'
                }}
              />

              {/* معلومات الصورة */}
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                p: 2,
                color: 'white'
              }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {selectedImages[currentImageIndex]?.description || 'بدون وصف'}
                </Typography>
                <Chip
                  label={selectedImages[currentImageIndex]?.type === 'coil' ? 'كوف' :
                         selectedImages[currentImageIndex]?.type === 'ruler' ? 'مسطرة' :
                         selectedImages[currentImageIndex]?.type === 'alternative_coil' ? 'كوف بديل' :
                         selectedImages[currentImageIndex]?.type === 'alternative_ruler' ? 'مسطرة بديلة' :
                         'تركيب'}
                  size="small"
                  sx={{ bgcolor: 'primary.main', color: 'white' }}
                />
              </Box>

              {/* أزرار التنقل */}
              {selectedImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.7)'
                      }
                    }}
                  >
                    <Box sx={{ transform: 'rotate(180deg)' }}>
                      ▶
                    </Box>
                  </IconButton>

                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.7)'
                      }
                    }}
                  >
                    ▶
                  </IconButton>
                </>
              )}
            </>
          )}
        </DialogContent>

        {/* مؤشرات الصور */}
        {selectedImages.length > 1 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            bgcolor: 'rgba(0, 0, 0, 0.8)'
          }}>
            {selectedImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: 60,
                  height: 40,
                  borderRadius: 1,
                  border: currentImageIndex === index ? 2 : 1,
                  borderColor: currentImageIndex === index ? 'primary.main' : 'grey.500',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  opacity: currentImageIndex === index ? 1 : 0.6,
                  transition: 'all 0.2s'
                }}
              >
                <Box
                  component="img"
                  src={selectedImages[index].url}
                  alt={`صورة ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default CoilAlternativesPage;
